import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { TimeEntry } from '../../entities/timeEntry/timeEntry';

@Component({
  selector: 'app-timeEntry-list-item',
  templateUrl: './timeEntry-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block'
  }
})
export class TimeEntryListItemComponent {

  // Inputs
  readonly timeEntry = input.required<TimeEntry>();
  readonly ownerId = input<string>();

  // State
  readonly menuVisible = signal(false);

  // Computed values
  readonly menuItems = computed(() => this.getMenuItems(this.timeEntry()));
  readonly shouldShowButton = computed(() => this.menuItems().length > 0);

  constructor() {
    effect(() => {
      // Trigger recomputation when timeEntry changes
      this.timeEntry();
    });
  }

  private getMenuItems(timeEntry: TimeEntry): MenuItem[] {
    // TODO: Implement actual menu items
    return [];
  }

  onMenuShow(): void {
    this.menuVisible.set(true);
  }

  onMenuHide(): void {
    this.menuVisible.set(false);
  }

  /**
   * Returns user short name (e.g., "Tom S.") or "XX" if not available
   */
  getUserShortName(): string {
    const timeEntry = this.timeEntry();
    if (!timeEntry?.user) {
      return 'XX';
    }
    const firstName = timeEntry.user.firstName || '';
    const lastName = timeEntry.user.lastName || '';

    if (!firstName && !lastName) {
      return 'XX';
    }

    return `${firstName} ${lastName.charAt(0)}.`.trim();
  }

  /**
   * Formats duration in seconds to hours format (e.g., "01:00 h", "00:30 h")
   */
  formatDurationToHours(seconds: number): string {
    if (!seconds && seconds !== 0) {
      return 'XX';
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const hoursStr = hours.toString().padStart(2, '0');
    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hoursStr}:${minutesStr} h`;
  }

  /**
 * Formats duration in seconds to "HH:mm h" format (e.g., "01:30 h", "00:30 h")
 */
formatDurationToStd(seconds: number): string {
  if (!seconds && seconds !== 0) {
    return '00:00 h';
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  return `${formattedHours}:${formattedMinutes} h`;
}

/**
 * Formats pause duration in seconds to "HH:mm h" format (e.g., "00:30 h", "01:15 h")
 */
formatPauseDuration(seconds: number): string {
  if (!seconds && seconds !== 0) {
    return '00:00 h';
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  return `${hoursStr}:${minutesStr} h`;
}
}
